import { useState, useEffect, useRef } from "react";

type DatePickerProps = {
  dateRange: string;
  onDateRangeChange: (range: string) => void;
};

export const DatePicker = ({ dateRange, onDateRangeChange }: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);
  
  const [currentMonth, setCurrentMonth] = useState(thirtyDaysAgo.getMonth());
  const [currentYear, setCurrentYear] = useState(thirtyDaysAgo.getFullYear());
  const [selectedDates, setSelectedDates] = useState<{start: Date, end: Date}>({
    start: thirtyDaysAgo,
    end: today
  });
  const [isSelectingRange, setIsSelectingRange] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const presetRanges = [
    "Today",
    "Yesterday", 
    "Last week",
    "Last month",
    "Last quarter"
  ];

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const formatDate = (date: Date) => {
    const day = date.getDate();
    const month = months[date.getMonth()].slice(0, 3);
    const year = date.getFullYear().toString().slice(-2);
    return `${day} ${month} ${year}`;
  };

  const handlePresetClick = (preset: string) => {
    const today = new Date();
    let startDate: Date, endDate: Date;
    
    switch (preset) {
      case "Today":
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case "Yesterday":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        endDate = new Date(startDate);
        break;
      case "Last week":
        endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 6);
        break;
      case "Last month":
        endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 29);
        break;
      case "Last quarter":
        endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 89);
        break;
      default:
        return;
    }
    
    setSelectedDates({ start: startDate, end: endDate });
    const range = `${formatDate(startDate)} — ${formatDate(endDate)}`;
    onDateRangeChange(range);
    setIsOpen(false);
  };

  const handleMonthChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const handleDateClick = (day: number) => {
    const clickedDate = new Date(currentYear, currentMonth, day);
    const today = new Date();
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    
    // Restrict date selection: no future dates beyond tomorrow, no dates older than 1 year
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    if (clickedDate > tomorrow || clickedDate < oneYearAgo) {
      return;
    }
    
    if (!isSelectingRange || !tempStartDate) {
      setTempStartDate(clickedDate);
      setIsSelectingRange(true);
    } else {
      const startDate = tempStartDate < clickedDate ? tempStartDate : clickedDate;
      const endDate = tempStartDate < clickedDate ? clickedDate : tempStartDate;
      
      setSelectedDates({ start: startDate, end: endDate });
      setIsSelectingRange(false);
      setTempStartDate(null);
      
      const range = `${formatDate(startDate)} — ${formatDate(endDate)}`;
      onDateRangeChange(range);
      setIsOpen(false);
    }
  };

  const isDateSelected = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    return (date.getTime() === selectedDates.start.getTime() || 
            date.getTime() === selectedDates.end.getTime()) ||
           (tempStartDate && date.getTime() === tempStartDate.getTime());
  };

  const isDateInRange = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    return date > selectedDates.start && date < selectedDates.end;
  };

  const isRangeStart = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    return date.getTime() === selectedDates.start.getTime();
  };

  const isRangeEnd = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    return date.getTime() === selectedDates.end.getTime();
  };

  const isDateDisabled = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    
    return date > tomorrow || date < oneYearAgo;
  };

  return (
    <div className="relative" ref={datePickerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="border border-gray-300 rounded px-4 py-2 flex items-center space-x-2"
      >
        <span className="text-gray-600">
          {dateRange || (() => {
            const today = new Date();
            const thirtyDaysAgo = new Date(today);
            thirtyDaysAgo.setDate(today.getDate() - 30);
            return `${formatDate(thirtyDaysAgo)} — ${formatDate(today)}`;
          })()}
        </span>
        <span className="text-gray-400">▼</span>
      </button>
      
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-80">
          <div className="flex">
            <div className="w-32 border-r border-gray-200 p-4">
              {presetRanges.map((preset) => (
                <button
                  key={preset}
                  onClick={() => handlePresetClick(preset)}
                  className="block w-full text-left py-2 px-2 hover:bg-gray-100 text-sm"
                >
                  {preset}
                </button>
              ))}
              <button
                onClick={() => {
                  const today = new Date();
                  const thirtyDaysAgo = new Date(today);
                  thirtyDaysAgo.setDate(today.getDate() - 30);
                  
                  setSelectedDates({ start: thirtyDaysAgo, end: today });
                  setIsSelectingRange(false);
                  setTempStartDate(null);
                  const range = `${formatDate(thirtyDaysAgo)} — ${formatDate(today)}`;
                  onDateRangeChange(range);
                  setIsOpen(false);
                }}
                className="block w-full text-left py-2 px-2 text-blue-600 text-sm mt-4"
              >
                Reset
              </button>
            </div>
            
            <div className="flex-1 p-4">
              <div className="flex justify-between items-center mb-4">
                <button 
                  onClick={() => handleMonthChange('prev')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ‹
                </button>
                <span className="font-medium">{months[currentMonth]} {currentYear}</span>
                <button 
                  onClick={() => handleMonthChange('next')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ›
                </button>
              </div>
              
              <div className="grid grid-cols-7 gap-1 text-xs text-center mb-2">
                {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(day => (
                  <div key={day} className="text-gray-500 py-1">{day}</div>
                ))}
              </div>
              
              <div className="grid grid-cols-7 text-sm">
                {Array.from({length: getDaysInMonth(currentMonth, currentYear)}, (_, i) => i + 1).map(day => (
                  <button
                    key={day}
                    onClick={() => handleDateClick(day)}
                    disabled={isDateDisabled(day)}
                    className={`py-1 px-1 ${
                      isDateDisabled(day) ? 'text-gray-300 cursor-not-allowed' :
                      isRangeStart(day) ? 'bg-blue-600 text-white rounded-l' :
                      isRangeEnd(day) ? 'bg-blue-600 text-white rounded-r' :
                      isDateSelected(day) ? 'bg-blue-600 text-white rounded' :
                      isDateInRange(day) ? 'bg-blue-100' : 'rounded hover:bg-blue-100'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};